import {AxiosPromise, InternalAxiosRequestConfig} from 'axios'
import axios, {parseResponseData} from '@/axiosInstance'
import {
    AgendaEventStatus,
    AgendaEventView,
    Availability,
    ConsultantNextAvailability,
    EventCategory,
    EventExtended,
    EventRoom,
    EventSmall,
    EventType
} from '@/features/agenda/types'
import {captureException} from '@sentry/react'
import {z} from 'zod'
import {NotificationPayload, ResponseType} from '@/types'
import {Access} from '@/features/accesses/types'

// Get agenda events
export type HttpGetEventsOptions = {
    params: {
        startDate?: string
        endDate?: string
        therapists?: string[]
        patients?: string[]
        projectId?: string
    }
}

export const httpGetEvents = async (options: HttpGetEventsOptions) => {
    const response = await axios.get(`events`, options)

    return parseResponseData({
        responseData: response.data,
        onZodError: captureException,
        responseShape: EventSmall.array()
    })
}

// Get agenda event by id
export type HttpGetEventOptions<TResponseType extends ResponseType> = {
    urlParams: {eventId: string}
    params: {responseType: TResponseType}
}

const HttpGetAgendaResponse = z.object({
    small: EventSmall,
    extended: EventExtended
})

export const httpGetEvent = async <TResponseType extends ResponseType>({
    urlParams,
    params
}: HttpGetEventOptions<TResponseType>) => {
    const response = await axios.get(`events/${urlParams.eventId}`, {params})
    return parseResponseData({
        responseData: response.data,
        onZodError: captureException,
        responseShape: HttpGetAgendaResponse.shape[params.responseType]
    })
}

// Payload for event
const BaseEventPayload = z
    .object({
        type: EventType,
        date: z.string(),
        category: EventCategory,
        status: AgendaEventStatus,
        sector: z.literal('outpatient'),
        time: z.string(),
        location: EventRoom,
        note: z.string().nullish(),
        isRemote: z.boolean().nullish(),
        isARecovery: z.boolean().nullable(),
        eventTeamMembers: z.array(
            z.object({
                treatmentRequestTeamId: z.string()
            })
        ),
        duration: z.number(),
        eventPatients: z.array(
            z.object({
                patientId: z.string(),
                projectId: z.string().nullish(),
                neuromuscularDiagnosisId: z.string().nullish()
            })
        ),
        eventConsultants: z.array(
            z.object({
                userId: z.string(),
                accessTypeId: z.string(),
                substituteId: z.string().nullish()
            })
        ),
        communicatedOnTime: z.boolean().nullish(),
        attachmentId: z.string().nullish(),
        isGroupTreatment: z.boolean().nullish(),
        remoteUrl: z.string().nullish(),
        missedAccessId: z.string().nullish(),
        isRepeated: z.boolean().nullish(),
        frequency: z.string().nullish(),
        position: z.number().nullish(),
        dayOfWeek: z.string().nullish(),
        absenceReason: z.string().nullish(),
        numberOfDays: z.number().nullish(),
        applyToFutureEvents: z.boolean().optional(),
        applySubstituteToFutureEvents: z.boolean().optional()
    })
    .extend(NotificationPayload.shape)
type BaseEventPayload = z.infer<typeof BaseEventPayload>

export const EventPayload = z.discriminatedUnion('type', [
    z.object({type: z.literal('event')}).merge(
        BaseEventPayload.pick({
            date: true,
            category: true,
            status: true,
            sector: true,
            time: true,
            location: true,
            note: true,
            isRemote: true,
            isARecovery: true,
            eventTeamMembers: true,
            duration: true,
            eventPatients: true,
            eventConsultants: true,
            isGroupTreatment: true,
            notificationRecipientIds: true,
            notificationDetails: true,
            remoteUrl: true,
            missedAccessId: true,
            isRepeated: true,
            frequency: true,
            position: true,
            dayOfWeek: true,
            applyToFutureEvents: true,
            applySubstituteToFutureEvents: true
        })
    ),
    z
        .object({
            type: z.literal('generic_event'),
            eventConsultants: z.array(
                z.object({
                    userId: z.string(),
                    accessTypeId: z.string().nullable()
                })
            )
        })
        .merge(
            BaseEventPayload.pick({
                date: true,
                time: true,
                duration: true,
                note: true,
                status: true,
                eventPatients: true,
                eventTeamMembers: true,
                sector: true,
                notificationRecipientIds: true,
                notificationDetails: true
            })
        ),
    z.object({type: z.literal('patient_absence')}).merge(
        BaseEventPayload.pick({
            date: true,
            eventPatients: true,
            communicatedOnTime: true,
            attachmentId: true,
            eventConsultants: true,
            eventTeamMembers: true,
            sector: true,
            notificationRecipientIds: true,
            notificationDetails: true,
            numberOfDays: true,
            applyToFutureEvents: true
        })
    ),
    z.object({type: z.literal('consultant_absence')}).merge(
        BaseEventPayload.pick({
            date: true,
            eventConsultants: true,
            eventPatients: true,
            eventTeamMembers: true,
            sector: true,
            absenceReason: true,
            time: true,
            duration: true,
            notificationRecipientIds: true,
            notificationDetails: true,
            numberOfDays: true,
            applyToFutureEvents: true
        })
    )
])
export type EventPayload = z.infer<typeof EventPayload>

// Edit event
export type HttpEditEventOptions = {
    urlParams: {eventId: string}
    payload: Partial<EventPayload>
}
export const httpEditEvent = async ({urlParams, payload}: HttpEditEventOptions) => {
    const response = await axios.patch(`events/${urlParams.eventId}`, payload)
    return parseResponseData({
        responseData: response.data,
        onZodError: captureException,
        responseShape: EventSmall.array()
    })
}

// Create event
export const HttpCreateEventOptions = z.object({
    payload: EventPayload
})
export type HttpCreateEventOptions = z.infer<typeof HttpCreateEventOptions>

export const httpCreateEvent = async ({payload}: HttpCreateEventOptions) => {
    const response = await axios.post(`events`, payload)
    return parseResponseData({
        responseData: response.data,
        onZodError: captureException,
        responseShape: EventSmall.array()
    })
}

// // Get events availability
export const HttpGetAvailabilitiesOptions = z.object({
    params: z.object({
        therapists: z.string().array(),
        startDate: z.string(),
        endDate: z.string()
    })
})
export type HttpGetAvailabilitiesOptions = z.infer<typeof HttpGetAvailabilitiesOptions>

export const httpGetAvailabilities = async ({params}: HttpGetAvailabilitiesOptions) => {
    const response = await axios.get(`events/availability`, {params})
    return parseResponseData({
        responseData: response.data,
        onZodError: captureException,
        responseShape: Availability
    })
}

// Get events count
export const HttpGetEventsCountResponse = z.object({
    numberOfEvents: z.number()
})
export type HttpGetEventsCountResponse = z.infer<typeof HttpGetEventsCountResponse>

export interface HttpGetEventsCountOptions {
    params: {
        startDate: string
        endDate: string
        therapists: string[]
        patients: string[]
    }
}
export const httpGetEventsCount = async (options: HttpGetEventsCountOptions) => {
    const response = await axios.get<HttpGetEventsCountResponse>(`events/count`, options)
    return parseResponseData({
        responseData: response.data,
        onZodError: captureException,
        responseShape: HttpGetEventsCountResponse
    })
}

// Delete event
export interface HttpDeleteEventOptions {
    urlParams: {
        eventId: string
    }
    params?: {
        applyToFutureEvents?: boolean
    }
    payload?: {
        notificationRecipientIds?: string[]
        notificationDetails?: string
    }
}
export const httpDeleteEvent = ({urlParams, payload, params}: HttpDeleteEventOptions) =>
    axios.delete(`events/${urlParams.eventId}`, {
        data: payload,
        params
    })

/* Mocked functions for testing purposes */
export type HttpGetEventViewsOptions = {
    urlParams: {userId: string}
    params: {
        target: 'users' | 'patients'
    }
}
export const httpGetEventViews = async ({urlParams, params}: HttpGetEventViewsOptions) => {
    const response = new Promise(resolve => {
        console.log('Get Event Views By User: ', urlParams, params)
        setTimeout(() => {
            resolve({
                headers: {},
                config: {} as InternalAxiosRequestConfig,
                data: [
                    {
                        id: '2',
                        name: 'Terapisti Ambulatoriali EE',
                        type: 'private'
                    },
                    {
                        id: '3',
                        name: 'Terapisti Domiciliari adulti',
                        type: 'private'
                    },
                    {
                        id: '4',
                        name: 'Terapisti Domiciliari EE',
                        type: 'private'
                    },
                    {
                        id: '5',
                        name: 'Terapisti Domiciliari pediatri',
                        type: 'private'
                    },
                    {
                        id: '6',
                        name: 'Terapisti Domiciliari misti',
                        type: 'private'
                    },
                    {
                        id: '7',
                        name: 'Medici',
                        type: 'public'
                    },
                    {
                        id: '8',
                        name: 'Fisioterapisti',
                        type: 'public'
                    },
                    {
                        id: '9',
                        name: 'Pediatri',
                        type: 'public'
                    },
                    {
                        id: '10',
                        name: 'Otorinolaringoiatri',
                        type: 'public'
                    },
                    {
                        id: '11',
                        name: 'Assistenti Sociali',
                        type: 'public'
                    },
                    {
                        id: '12',
                        name: 'Psicologi',
                        type: 'public'
                    }
                ],
                status: 200,
                statusText: 'ok'
            })
        }, 1000)
    }) as AxiosPromise

    const result = await response

    return parseResponseData({
        responseData: result.data,
        onZodError: captureException,
        responseShape: AgendaEventView.array()
    })
}

export interface HttpGetValidatedAccessesOptions {
    params: {
        startDate: string
        endDate: string
        patient: string
        type: 'absence' | 'access'
    }
}
export const httpGetValidatedAccesses = async (options: HttpGetValidatedAccessesOptions) => {
    const response = await axios.get(`events/validated-accesses`, {params: options.params})
    return parseResponseData({
        responseData: response.data,
        onZodError: captureException,
        responseShape: z.array(Access)
    })
}

export const HttpGetNextAvailabilityOptions = z.object({
    params: z.object({
        therapists: z.string().array(),
        startDate: z.string(),
        endDate: z.string()
    })
})
export type HttpGetNextAvailabilityOptions = z.infer<typeof HttpGetNextAvailabilityOptions>

export const httpGetNextAvailability = async (options: HttpGetNextAvailabilityOptions) => {
    const response = await axios.get(`events/next-availability`, {params: options.params})
    return parseResponseData({
        responseData: response.data,
        onZodError: captureException,
        responseShape: ConsultantNextAvailability.array()
    })
}
